import { Icon, IconName } from '@repo/ui';
import c from 'classnames';
import MobileMenu from 'components/MobileMenu/MobileMenu';
import { AnimatePresence } from 'framer-motion';
import useGetParsedPartnerNotes from 'hooks/partnerNotes/mobile/useGetMobilePartnerNotesItems';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useNavKitId } from 'hooks/useNavKitId';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useFeature from 'services/features/useFeature';
import styles from './MobileNav.module.css';
import getNavItemParameters from './utils/getNavItemParameters';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';

export const MobileNavItem = ({
  pathname,
  iconName,
  activeIconName,
  label,
  tinyAccountId,
  color = 'green',
  iconClassName,
  onClick,
}: {
  pathname: string;
  iconName: IconName;
  activeIconName?: IconName;
  label: string;
  tinyAccountId?: string | null;
  color?: string;
  iconClassName?: string;
  onClick?: VoidFunction;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = useMemo(() => {
    return pathname.length
      ? location.pathname.startsWith(`/${pathname}`)
      : location.pathname === '/';
  }, [location, pathname]);

  return (
    <div
      className={styles.navMenuItem}
      onClick={() => {
        if (onClick) onClick();
        navigate(`/${pathname}`, {
          state: { tinyAccountId, from: location.pathname },
        });
      }}
    >
      <Icon
        name={isActive && activeIconName ? activeIconName : iconName}
        className={c(
          isActive && styles[color],
          iconClassName && styles[iconClassName],
          isActive && styles.activeIcon,
        )}
      />
      <span className={c(styles.navMenuItemLabel, isActive && styles[color])}>
        {label}
      </span>
    </div>
  );
};

interface MobileNavProps {
  isOpen: boolean;
  onToggle: () => void;
}

const MobileNav = ({
  isOpen: mobileMenuOpen,
  onToggle: toggleMobileMenu,
}: MobileNavProps) => {
  const { currentTinyAccountId } = useCurrentKitAndTinyAccount();
  const navKitId = useNavKitId();

  const { trackEvent } = useAnalytics();

  const [partnerNote] = useGetParsedPartnerNotes();

  const { isFeatureEnabled: isPartnerRecommendationNoteEnabled } = useFeature(
    'partner_recommendation_note',
  );

  return (
    <>
      <AnimatePresence>
        {mobileMenuOpen && <MobileMenu toggleMobileMenu={toggleMobileMenu} />}
      </AnimatePresence>
      <div className={styles.mobileNav}>
        <MobileNavItem
          pathname={``}
          iconName='home'
          label='Home'
          tinyAccountId={currentTinyAccountId}
        />
        <MobileNavItem
          pathname={`results/${navKitId ?? ''}`}
          iconName='lightOn'
          label='Results'
        />
        <MobileNavItem
          pathname={`action-plan/${navKitId ?? ''}`}
          iconName='today'
          label='Action'
        />

        <MobileNavItem
          {...getNavItemParameters({
            partnerNote,
            isPartnerRecommendationNoteEnabled,
            trackEvent,
          })}
        />

        <div className={styles.navMenuItem} onClick={toggleMobileMenu}>
          <Icon name='hamburger' active={mobileMenuOpen} />
          <span
            className={c(
              styles.navMenuItemLabel,
              mobileMenuOpen && styles.active,
            )}
          >
            Menu
          </span>
        </div>
      </div>
    </>
  );
};
export default MobileNav;
